import React, { useState, useEffect } from "react";
// @ts-ignore
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ListGroup, ListGroupItem } from "components/Shared";
import { translate } from "lib";
import ReactDOM from "react-dom";

interface Props {
  conditions: any[];
  onUpdate: (conditions: any[]) => void;
  onAdd: () => void;
}

// Helper function to properly merge styles for dragging
const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  // styles we need for the clone to be visible
  userSelect: "none",
  background: isDragging ? "#f0f0f0" : "transparent",
  
  // styles from react-beautiful-dnd
  ...draggableStyle
});

// Portal for rendering the dragged item outside modal boundaries
const portal: HTMLElement = document.createElement('div');
document.body.appendChild(portal);

const DraggableConditions = ({ conditions, onUpdate, onAdd }: Props) => {
  const [localConditions, setLocalConditions] = useState(conditions);

  useEffect(() => {
    setLocalConditions(conditions);
  }, [conditions]);

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const reorderedConditions = Array.from(localConditions);
    const [removed] = reorderedConditions.splice(result.source.index, 1);
    reorderedConditions.splice(result.destination.index, 0, removed);

    // Update index for all conditions
    const conditionsWithNewIndex = reorderedConditions.map((condition, index) => ({
      ...condition,
      index: index
    }));

    setLocalConditions(conditionsWithNewIndex);
    onUpdate(conditionsWithNewIndex);
  };

  const handleRemove = (conditionId: string) => {
    const updatedConditions = localConditions.filter(c => c.id !== conditionId);
    setLocalConditions(updatedConditions);
    onUpdate(updatedConditions);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="conditions-droppable">
        {(provided: any, snapshot: any) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              background: snapshot.isDraggingOver ? 'rgba(0, 123, 255, 0.05)' : 'transparent',
            }}
          >
            <ListGroup
              resource="conditions"
              collection={localConditions}
              pagination={false}
              onAdd={onAdd}
              renderRow={(condition: any) => (
                <Draggable 
                  key={condition.id} 
                  draggableId={condition.id} 
                  index={localConditions.indexOf(condition)}
                >
                  {(provided: any, snapshot: any) => {
                    const child = (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <ListGroupItem
                          key={condition.id}
                          title={translate(condition.kind)}
                          heading={condition.name}
                          onRemove={() => handleRemove(condition.id)}
                        />
                      </div>
                    );

                    // Render dragging item in a portal to escape modal overflow constraints
                    if (snapshot.isDragging) {
                      return ReactDOM.createPortal(child, portal);
                    }
                    return child;
                  }}
                </Draggable>
              )}
            />
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableConditions;